/**
 * useImpactEvents
 *
 * Fetches GeoJSON catalysts from the Impact backend (GET /api/catalysts).
 * Keyed by date range + map bounds so results update as the user pans/zooms.
 *
 * At low zoom levels the backend returns clustered centroids (cluster=true).
 * At high zoom we request individual points (cluster=false) so Mapbox GL
 * can render per-pin markers.
 */
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/axios';
import { useMapStore } from '@/stores/mapStore';
import { MAP_DEFAULTS } from '@/lib/constants';
import { useAuth } from '@/components/auth/AuthContext';

export function useImpactEvents() {
  const { isAuthenticated } = useAuth();
  const dateRange = useMapStore((s) => s.dateRange);
  const mapBounds = useMapStore((s) => s.mapBounds);
  const mapZoom = useMapStore((s) => s.mapZoom);

  const clustered = mapZoom < MAP_DEFAULTS.PIN_ZOOM_THRESHOLD;

  return useQuery({
    queryKey: ['impactEvents', dateRange, mapBounds, clustered],
    queryFn: async ({ signal }) => {
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        cluster: clustered,
      };

      if (mapBounds) {
        // mapBounds is [west, south, east, north]
        const [min_lon, min_lat, max_lon, max_lat] = mapBounds;
        params.min_lat = min_lat;
        params.min_lon = min_lon;
        params.max_lat = max_lat;
        params.max_lon = max_lon;
      }

      const { data } = await apiClient.get('/api/catalysts', { params, signal });
      // Guard against unexpected non-GeoJSON responses (e.g. Drupal 403 HTML
      // returned before the permission is granted to anonymous/authenticated).
      if (!data || data.type !== 'FeatureCollection') {
        return { type: 'FeatureCollection', features: [] };
      }
      return data;
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    enabled: isAuthenticated,
  });
}

/**
 * useImpactEventById — fetch a single catalyst's full GeoJSON Feature.
 * @param {string} uuid  UUID of the Catalyst
 */
export function useImpactEventById(uuid) {
  return useQuery({
    queryKey: ['impactEvent', uuid],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get(`/api/catalysts/${uuid}`, { signal });
      return data; // GeoJSON Feature
    },
    enabled: !!uuid,
    staleTime: 5 * 60_000,
  });
}
